/**
 * VITOMALIA HUNDERASSEN-ÜBERSICHT — Section JS
 * Vanilla ES2018+. Defer-loaded.
 *
 * Features:
 *  - Live-Suche (canonical_name + alternativnamen, Diakritika-norm.)
 *  - Filter-Bar (Tier-2 immer sichtbar + Tier-3 toggle)
 *  - Multi-Select pro Filter, OR within axis, AND across axes
 *  - URL-Persistenz via ?kat=…&qz=…&groesse=… etc.
 *  - Vergleichsleiste (max 3) + Modal mit Diff-Highlight
 *  - Quick-Start-Profile setzen mehrere Filter gleichzeitig
 *  - Empty-State + Reset-Button
 */
(function () {
  'use strict';

  const root = document.querySelector('[data-vmrasse-root]');
  if (!root) return;

  // Native descriptions keep the existing explanations available without a
  // separate tooltip asset, which this theme does not contain.
  root.querySelectorAll('[data-tooltip]').forEach(el => {
    el.setAttribute('title', el.getAttribute('data-tooltip'));
  });

  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Data
  let DATA = [];
  try {
    const dataEl = root.querySelector('#vmrasse-data');
    DATA = dataEl ? JSON.parse(dataEl.textContent) : [];
  } catch (e) { DATA = []; }

  // ---------------------------------------------------- Utils
  const normalize = s => (s||'').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g,'').replace(/ß/g,'ss').replace(/œ/g,'oe').replace(/æ/g,'ae');
  const escapeHtml = s => String(s||'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#39;');
  const debounce = (fn,ms)=>{let t;return function(){const a=arguments,c=this;clearTimeout(t);t=setTimeout(()=>fn.apply(c,a),ms);};};

  // ---------------------------------------------------- State
  const STATE = {
    search: '',
    filters: {
      vit: new Set(),
      groesse: new Set(),
      aktivitaet: new Set(),
      anfaenger: new Set(),
      qualzucht: new Set(),
      gesundheit: new Set(),
      pflege: new Set(),
      hitze: new Set(),
      kaelte: new Set(),       // Phase 3.3 (Phase-2-NEU)
      nutztier: new Set(),     // Phase 3.3 (Schuld 12.14: ehemals Konflikt-Sub, jetzt Tier-3-Filter)
      schaedel: new Set(),     // Phase 3.3 v2 (Schuld 12.15: ersetzt VIT-14)
      sport: new Set(),
      kinder: new Set(),
      fell: new Set(),
      fci: new Set(),
      ursprung: new Set(),
    },
    sort: 'alpha',  // Phase 3.3: alpha (Default) | qualzucht | aktivitaet | groesse
    compare: new Set(),
  };

  const FILTER_KEYS = Object.keys(STATE.filters);
  const FIELD_MAP = {
    vit:        'vit_codes',           // array
    groesse:    'groesse_kategorie',
    aktivitaet: 'aktivitaet',
    anfaenger:  'anfaenger_geeignet',
    qualzucht:  'qualzucht_ampel',  // Phase 3.1: liest jetzt das Phase-2-Metafeld (Schuld 12.12)
    gesundheit: 'gesundheitsrisiko',
    pflege:     'pflegeaufwand',
    hitze:      'hitze_vertraeglichkeit',
    kaelte:     'kaeltevertraeglichkeit',         // Phase 3.3
    nutztier:   'score_nutztierrisiko',           // Phase 3.3 (numerisch 1-5)
    schaedel:   'schaedelform',                   // Phase 3.3 v2 (Schuld 12.15)
    sport:      'sporteignung',
    kinder:     'kinder_eignung',
    fell:       'fell_typ',            // array
    fci:        'fci_gruppe',
    ursprung:   'ursprung',
  };

  // ---------------------------------------------------- URL state
  function readUrl() {
    const sp = new URLSearchParams(location.search);
    FILTER_KEYS.forEach(k => {
      const raw = sp.get(k === 'qualzucht' ? 'qz' : k.slice(0,3));
      if (raw) raw.split(',').forEach(v => STATE.filters[k].add(v));
    });
    const q = sp.get('q'); if (q) STATE.search = q;
  }
  function writeUrl() {
    const sp = new URLSearchParams(location.search);
    if (!activeSmart) sp.delete('preset');
    FILTER_KEYS.forEach(k => {
      const param = (k === 'qualzucht') ? 'qz' : k.slice(0,3);
      const set = STATE.filters[k];
      if (set.size) sp.set(param, Array.from(set).join(','));
      else sp.delete(param);
    });
    if (STATE.search) sp.set('q', STATE.search); else sp.delete('q');
    history.replaceState({}, '', location.pathname + (sp.toString() ? '?' + sp : '') + location.hash);
  }

  // ---------------------------------------------------- Filtering
  // Phase 3.3 v2: Search erweitert auf canonical_name + alternativnamen + kurz_charakter + vit-descriptions
  const VIT_DESCS_FALLBACK = {
    'VIT-01': 'Hütehund Treiben Hüten',
    'VIT-02': 'Schutz Service Nervenstärke',
    'VIT-03': 'Wachhund Molosser territorial',
    'VIT-04': 'Pinscher Schnauzer Hofwächter',
    'VIT-05': 'Terrier beutemotiviert',
    'VIT-06': 'Dackel Niederlauf Baujäger',
    'VIT-07': 'Vorstehhund Apport Vorsteh',
    'VIT-08': 'Bracke Scenthound Spurarbeit',
    'VIT-09': 'Apportierhund kooperativ',
    'VIT-10': 'Spaniel Stöberhund Federwild',
    'VIT-11': 'Schlittenhund Zughund Ausdauer',
    'VIT-12': 'Spitz Urtyp eigenständig',
    'VIT-13': 'Toy Begleithund Schoßhund',
    'VIT-15': 'Sighthound Sichtjäger Hetzjäger Beutetrieb',
  };

  function profileMatches(p) {
    if (STATE.search) {
      const q = normalize(STATE.search);
      const vitWords = (p.vit_codes||[]).map(c => VIT_DESCS_FALLBACK[c] || '').join(' ');
      const hay = [
        p.title,
        ...(p.alternativnamen||[]),
        p.kurz_charakter || '',
        vitWords
      ].map(normalize).join(' ');
      if (!hay.includes(q)) return false;
    }
    for (const [key, set] of Object.entries(STATE.filters)) {
      if (set.size === 0) continue;
      const field = FIELD_MAP[key];
      let val = p[field];
      // Phase 3.3 v2: numerische Felder (z.B. score_nutztierrisiko) als String vergleichen
      if (key === 'nutztier' && val !== '' && val != null) {
        // Die gespeicherten Scores enthalten auch Zwischenwerte wie 1.8 und 4.5.
        val = String(Math.max(1, Math.min(5, Math.round(Number(val)))));
      } else if (typeof val === 'number') val = String(val);
      else if (typeof val === 'string' && /^[0-9.]+$/.test(val)) {
        const f = parseFloat(val);
        if (!isNaN(f) && Math.floor(f) === f) val = String(Math.floor(f));
      }
      if (key === 'kinder') {
        if (val === 'gut') val = 'ja';
        if (val === 'bedingt') val = 'mit_einschraenkungen';
      }
      let matched = false;
      if (Array.isArray(val)) {
        for (const sel of set) if (val.includes(sel)) { matched = true; break; }
      } else if (val) {
        matched = set.has(val);
      }
      if (!matched) return false;
    }
    return true;
  }

  // ---------------------------------------------------- Render
  // Phase 3.3: Sortier-Mapping
  const GROESSE_RANK = { sehr_klein: 1, klein: 2, klein_mittel: 3, mittel: 4, mittel_gross: 5, gross: 6, sehr_gross: 7 };
  const AKTIV_RANK = { niedrig: 1, moderat: 2, hoch: 3, sehr_hoch: 4 };

  function sortKeyFor(p) {
    switch (STATE.sort) {
      case 'qualzucht':
        // 0..4 — Unbedenklich first (aufsteigend)
        return parseFloat(p.score_qualzucht || 0) || 0;
      case 'aktivitaet':
        return AKTIV_RANK[p.aktivitaet] || 0;
      case 'groesse':
        return GROESSE_RANK[p.groesse_kategorie] || 0;
      case 'alpha':
      default:
        return (p.title || '').toLowerCase();
    }
  }

  function applySortToDom() {
    const grid = root.querySelector('[data-vmrasse-grid]');
    if (!grid) return;
    const cards = Array.from(grid.querySelectorAll('[data-vmrasse-card]'));
    cards.sort((a, b) => {
      const pA = DATA.find(x => x.profile_id === a.getAttribute('data-vmrasse-card'));
      const pB = DATA.find(x => x.profile_id === b.getAttribute('data-vmrasse-card'));
      if (!pA || !pB) return 0;
      const ka = sortKeyFor(pA), kb = sortKeyFor(pB);
      if (typeof ka === 'string' || typeof kb === 'string') return String(ka).localeCompare(String(kb), 'de');
      return ka - kb;
    });
    cards.forEach(c => grid.appendChild(c));
  }

  function applyFilters() {
    let visibleCount = 0;
    const cards = root.querySelectorAll('[data-vmrasse-card]');
    cards.forEach(card => {
      const id = card.getAttribute('data-vmrasse-card');
      const p = DATA.find(x => x.profile_id === id);
      if (!p) { card.hidden = true; return; }
      const m = profileMatches(p);
      card.hidden = !m;
      if (m) visibleCount++;
    });
    // Counter (Phase-3.1-Format mit "X von Y" oder nur "X")
    const counter = root.querySelector('[data-vmrasse-counter]');
    if (counter) {
      const total = DATA.length;
      counter.innerHTML = visibleCount === total
        ? `<strong data-vmrasse-counter-current>${total}</strong> ${total === 1 ? 'Rasse' : 'Rassen'}`
        : `<strong data-vmrasse-counter-current>${visibleCount}</strong> von <strong>${total}</strong> ${total === 1 ? 'Rasse' : 'Rassen'}`;
    }
    // Empty
    const empty = root.querySelector('[data-vmrasse-empty]');
    if (empty) empty.setAttribute('data-active', visibleCount === 0 ? 'true' : 'false');
    // Active chips + reset
    renderChips();
    // Mobile-Bar: aktive Filter-Anzahl als Badge
    const activeCount = FILTER_KEYS.reduce((n, k) => n + STATE.filters[k].size, 0) + (STATE.search ? 1 : 0);
    root.querySelectorAll('[data-vmrasse-active-count]').forEach(el => {
      el.textContent = activeCount;
      el.setAttribute('data-active-count', String(activeCount));
    });
    // Sort
    applySortToDom();
  }

  function renderChips() {
    const chipsBox = root.querySelector('[data-vmrasse-chips]');
    if (!chipsBox) return;
    let html = '';
    let any = false;
    if (STATE.search) {
      any = true;
      html += `<span class="vmrasse__chip">Suche: ${escapeHtml(STATE.search)}<button type="button" class="vmrasse__chip-remove" data-vmrasse-chip-remove="search" aria-label="Suche zurücksetzen">×</button></span>`;
    }
    Object.entries(STATE.filters).forEach(([key, set]) => {
      set.forEach(v => {
        any = true;
        const option = Array.from(root.querySelectorAll('[data-vmrasse-filter]')).find(el => el.dataset.vmrasseFilter === key && el.dataset.vmrasseValue === v);
        const group = root.querySelector(`[data-vmrasse-accordion="${key === 'vit' ? 'funktion' : key}"] .vmrasse__accordion-title`);
        const label = `${group ? group.textContent.trim() : key}: ${option ? option.textContent.trim() : v}`;
        html += `<span class="vmrasse__chip">${escapeHtml(label)}<button type="button" class="vmrasse__chip-remove" data-vmrasse-chip-remove="${escapeHtml(key+'|'+v)}" aria-label="${escapeHtml(label)} entfernen">×</button></span>`;
      });
    });
    chipsBox.innerHTML = html;
    chipsBox.setAttribute('data-has-active', any ? 'true' : 'false');
    // Pill states
    root.querySelectorAll('[data-vmrasse-filter]').forEach(el => {
      const key = el.getAttribute('data-vmrasse-filter');
      const val = el.getAttribute('data-vmrasse-value');
      if (!key || !val) return;
      const active = STATE.filters[key] && STATE.filters[key].has(val);
      el.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  // ---------------------------------------------------- Filter actions
  function toggleFilter(key, val) {
    if (!STATE.filters[key]) return;
    activeSmart = null;
    syncSmartTilesFromState();
    if (STATE.filters[key].has(val)) STATE.filters[key].delete(val);
    else STATE.filters[key].add(val);
    writeUrl();
    applyFilters();
  }
  function clearFilter(key, val) {
    activeSmart = null;
    syncSmartTilesFromState();
    if (val) STATE.filters[key].delete(val);
    else STATE.filters[key].clear();
    writeUrl();
    applyFilters();
  }
  function resetAll() {
    STATE.search = '';
    Object.values(STATE.filters).forEach(s => s.clear());
    const inp = root.querySelector('[data-vmrasse-search-input]');
    if (inp) inp.value = '';
    // Phase 3.6 fix: Smart-Filter + Sortier + URL-Preset auch resetten
    if (typeof activeSmart !== 'undefined') {
      activeSmart = null;
      root.querySelectorAll('[data-vmrasse-smart]').forEach(t => t.setAttribute('aria-pressed', 'false'));
    }
    STATE.sort = 'alpha';
    const sortSel = root.querySelector('[data-vmrasse-sort]');
    if (sortSel) sortSel.value = 'alpha';
    // Nur Parameter dieses Filters entfernen; Theme-Vorschau und andere Parameter erhalten.
    const sp = new URLSearchParams(location.search);
    ['q', 'sort', 'preset', ...FILTER_KEYS.map(k => k === 'qualzucht' ? 'qz' : k.slice(0, 3))].forEach(k => sp.delete(k));
    history.replaceState({}, '', location.pathname + (sp.toString() ? '?' + sp : '') + location.hash);
    writeUrl();
    applyFilters();
  }

  // ---------------------------------------------------- Wire UI
  function wire() {
    // Pills
    root.querySelectorAll('[data-vmrasse-filter]').forEach(el => {
      el.addEventListener('click', e => {
        e.preventDefault();
        const key = el.getAttribute('data-vmrasse-filter');
        const val = el.getAttribute('data-vmrasse-value');
        toggleFilter(key, val);
      });
    });

    // Tier-3 toggle
    const t3btn = root.querySelector('[data-vmrasse-tier3-toggle]');
    const t3box = root.querySelector('[data-vmrasse-tier3]');
    if (t3btn && t3box) {
      t3btn.addEventListener('click', () => {
        const open = t3box.getAttribute('data-open') === 'true';
        t3box.setAttribute('data-open', open ? 'false' : 'true');
        t3btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        t3btn.textContent = open ? 'Erweiterte Filter ▾' : 'Erweiterte Filter ▴';
      });
    }

    // Mobile filter sheet toggle
    const mbtn = root.querySelector('[data-vmrasse-filter-toggle-mobile]');
    const filtersBox = root.querySelector('[data-vmrasse-filters]');
    if (mbtn && filtersBox) {
      mbtn.addEventListener('click', () => {
        const open = filtersBox.getAttribute('data-mobile-open') === 'true';
        filtersBox.setAttribute('data-mobile-open', open ? 'false' : 'true');
      });
    }

    // Search input
    const search = root.querySelector('[data-vmrasse-search-input]');
    if (search) {
      search.value = STATE.search;
      search.addEventListener('input', debounce(() => {
        STATE.search = search.value.trim();
        writeUrl();
        applyFilters();
      }, 80));
    }

    // Quick-start
    root.querySelectorAll('[data-vmrasse-quickstart]').forEach(el => {
      el.addEventListener('click', e => {
        e.preventDefault();
        const cfg = el.getAttribute('data-vmrasse-filter-config');
        if (!cfg) return;
        // Format: "key:val,val|key:val"
        Object.values(STATE.filters).forEach(s => s.clear());
        cfg.split('|').forEach(part => {
          const [k, vs] = part.split(':');
          if (!k || !vs) return;
          if (STATE.filters[k]) vs.split(',').forEach(v => STATE.filters[k].add(v.trim()));
        });
        writeUrl();
        applyFilters();
        const grid = root.querySelector('[data-vmrasse-grid]');
        if (grid) grid.scrollIntoView({behavior: reduceMotion ? 'auto' : 'smooth', block: 'start'});
      });
    });

    // Chip removal
    root.addEventListener('click', e => {
      const btn = e.target.closest('[data-vmrasse-chip-remove]');
      if (!btn) return;
      const v = btn.getAttribute('data-vmrasse-chip-remove');
      if (v === 'search') {
        STATE.search = '';
        const i = root.querySelector('[data-vmrasse-search-input]'); if (i) i.value = '';
      } else {
        const [key, val] = v.split('|');
        clearFilter(key, val);
        return;
      }
      writeUrl();
      applyFilters();
    });

    // Reset all
    const reset = root.querySelector('[data-vmrasse-reset]');
    if (reset) reset.addEventListener('click', e => { e.preventDefault(); resetAll(); });
    const emptyReset = root.querySelector('[data-vmrasse-empty-reset]');
    if (emptyReset) emptyReset.addEventListener('click', e => { e.preventDefault(); resetAll(); });

    // Compare toggle
    root.addEventListener('click', e => {
      const btn = e.target.closest('[data-vmrasse-compare-toggle]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      const id = btn.getAttribute('data-vmrasse-compare-toggle');
      if (STATE.compare.has(id)) STATE.compare.delete(id);
      else if (STATE.compare.size < 3) STATE.compare.add(id);
      else { alert('Maximal 3 Rassen vergleichbar.'); return; }
      renderCompareBar();
    });

    // Compare bar buttons
    const cBtn = root.querySelector('[data-vmrasse-compare-open]');
    if (cBtn) cBtn.addEventListener('click', openCompareModal);
    const cClr = root.querySelector('[data-vmrasse-compare-clear]');
    if (cClr) cClr.addEventListener('click', () => { STATE.compare.clear(); renderCompareBar(); });

    // Modal close
    const mClose = root.querySelector('[data-vmrasse-modal-close]');
    if (mClose) mClose.addEventListener('click', closeCompareModal);
    const mOverlay = root.querySelector('[data-vmrasse-modal]');
    if (mOverlay) mOverlay.addEventListener('click', e => {
      if (e.target === mOverlay) closeCompareModal();
    });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') {
        closeCompareModal();
        const filtersBox = root.querySelector('[data-vmrasse-filters]');
        if (filtersBox && filtersBox.getAttribute('data-mobile-open') === 'true') {
          filtersBox.setAttribute('data-mobile-open', 'false');
        }
      }
    });
  }

  // ---------------------------------------------------- Compare bar
  function renderCompareBar() {
    const bar = root.querySelector('[data-vmrasse-compare-bar]');
    const list = root.querySelector('[data-vmrasse-compare-list]');
    if (!bar || !list) return;
    if (STATE.compare.size === 0) {
      bar.setAttribute('data-visible', 'false');
    } else {
      bar.setAttribute('data-visible', 'true');
      list.innerHTML = Array.from(STATE.compare).map(id => {
        const p = DATA.find(x => x.profile_id === id);
        if (!p) return '';
        const name = (p.title || '').split(':')[0].trim();
        return `<span class="vmrasse__compare-mini"><span class="vmrasse__compare-mini-name">${escapeHtml(name)}</span><button type="button" class="vmrasse__compare-mini-remove" data-vmrasse-compare-toggle="${escapeHtml(id)}" aria-label="${escapeHtml(name)} entfernen">×</button></span>`;
      }).join('');
    }
    // Update card-compare button states
    root.querySelectorAll('[data-vmrasse-compare-toggle]').forEach(b => {
      const id = b.getAttribute('data-vmrasse-compare-toggle');
      b.setAttribute('aria-pressed', STATE.compare.has(id) ? 'true' : 'false');
    });
  }

  // ---------------------------------------------------- Compare modal
  // ==============================================
  // Phase 3.4 — Vergleichs-Modal mit 4 Sektionen
  // ==============================================
  function scoreToInt(v) {
    if (v === '' || v == null) return 0;
    const f = parseFloat(v);
    return isNaN(f) ? 0 : Math.round(f);
  }

  function renderBar(score, max) {
    score = Math.max(0, Math.min(max, score));
    let html = '<span class="vmrasse__cmp-bar" data-score="' + score + '">';
    for (let i = 1; i <= max; i++) {
      html += '<span class="vmrasse__cmp-bar-step' + (i <= score ? ' vmrasse__cmp-bar-step--on' : '') + '"></span>';
    }
    html += '</span>';
    return html;
  }

  function renderHeroPills(it) {
    const qz = it.qualzucht_ampel || '';
    const qzLabels = { 'grün': 'Unbedenklich', gelb: 'Mit Vorsicht', orange: 'Bedenklich', rot: 'Kritisch' };
    const qzClass = { 'grün': 'green', gelb: 'yellow', orange: 'orange', rot: 'red' }[qz] || 'neutral';
    const qzPill = '<span class="vmrasse__cmp-hero-pill vmrasse__cmp-hero-pill--' + qzClass + '" title="Tierschutzfachliche Zuchteinordnung">Zucht: ' + escapeHtml(qzLabels[qz] || 'Keine Angabe') + '</span>';

    // Schädelform-Pille
    const sk = it.schaedelform || '—';
    const skLabels = { brachycephal: 'Brachycephal', mesozephal: 'Mesozephal', dolichocephal: 'Dolichocephal' };
    const skPill = '<span class="vmrasse__cmp-hero-pill vmrasse__cmp-hero-pill--neutral" title="Schädelform">' + escapeHtml(skLabels[sk] || sk) + '</span>';

    return '<div class="vmrasse__cmp-hero-pills">' + qzPill + skPill + '</div>';
  }

  function renderProfileHeader(it) {
    const img = it.image
      ? '<img src="' + escapeHtml(it.image) + '" alt="" loading="lazy" class="vmrasse__cmp-head-img">'
      : '<div class="vmrasse__cmp-head-fallback">' + escapeHtml((it.title || '?')[0].toUpperCase()) + '</div>';
    const canonical = (it.title || '').split(':')[0].trim();
    const removeBtn = '<button type="button" class="vmrasse__cmp-head-remove" data-vmrasse-compare-toggle="' + escapeHtml(it.profile_id) + '" aria-label="Aus Vergleich entfernen">×</button>';
    return (
      '<div class="vmrasse__cmp-head" data-cmp-profile="' + escapeHtml(it.profile_id) + '">' +
        removeBtn +
        img +
        '<div class="vmrasse__cmp-head-name">' + escapeHtml(canonical) + '</div>' +
        renderHeroPills(it) +
        '<a class="vmrasse__cmp-head-link" href="' + escapeHtml(it.url || '#') + '">Profil ansehen →</a>' +
      '</div>'
    );
  }

  function renderSection(label, title, items, scoreSpecs, opts) {
    opts = opts || {};
    let html = '<section class="vmrasse__cmp-section">';
    html += '<header class="vmrasse__cmp-section-header"><span class="vmrasse__cmp-section-label">' + label + '</span><h3 class="vmrasse__cmp-section-title">' + escapeHtml(title) + '</h3></header>';
    html += '<div class="vmrasse__cmp-rows">';
    for (const [scoreLabel, field, tooltip, max] of scoreSpecs) {
      const m = max || 5;
      html += '<div class="vmrasse__cmp-row" data-tooltip="' + escapeHtml(tooltip) + '">';
      html += '<div class="vmrasse__cmp-row-label">' + escapeHtml(scoreLabel) + '</div>';
      html += '<div class="vmrasse__cmp-row-cells" style="grid-template-columns: repeat(' + items.length + ', 1fr)">';
      for (const it of items) {
        const v = scoreToInt(it[field]);
        html += '<div class="vmrasse__cmp-cell">' + renderBar(v, m) + '<span class="vmrasse__cmp-cell-val">' + (v > 0 ? v + '/' + m : '—') + '</span></div>';
      }
      html += '</div></div>';
    }
    html += '</div>';

    // Optionale Quotes pro Profil (Sektion B: aggressionsprofil_kurz)
    if (opts.quotesField) {
      html += '<div class="vmrasse__cmp-quotes">';
      for (const it of items) {
        const q = (it[opts.quotesField] || '').trim();
        const canonical = (it.title || '').split(':')[0].trim();
        html += '<div class="vmrasse__cmp-quote"><strong>' + escapeHtml(canonical) + ':</strong> <em>' + escapeHtml(q || '—') + '</em></div>';
      }
      html += '</div>';
    }

    if (opts.methodikHinweis) {
      html += '<p class="vmrasse__cmp-methodik">' + escapeHtml(opts.methodikHinweis) + '</p>';
    }
    if (opts.erklaerungsBox) {
      html += '<div class="vmrasse__cmp-erklaerung">' + escapeHtml(opts.erklaerungsBox) + '</div>';
    }

    html += '</section>';
    return html;
  }

  function renderOverview(items) {
    const labels = {
      sehr_klein: 'Sehr klein', klein: 'Klein', klein_mittel: 'Klein–mittel', mittel: 'Mittel',
      mittel_gross: 'Mittel–groß', gross: 'Groß', sehr_gross: 'Sehr groß',
      niedrig: 'Niedrig', moderat: 'Moderat', hoch: 'Hoch', sehr_hoch: 'Sehr hoch',
      ja: 'Ja', bedingt: 'Bedingt', nein: 'Nein', gering: 'Gering'
    };
    const rows = [
      ['Größe', 'groesse_kategorie'], ['Aktivität', 'aktivitaet'],
      ['Für Anfänger', 'anfaenger_geeignet'], ['Gesundheitsrisiko', 'gesundheitsrisiko']
    ];
    return '<section class="vmrasse__cmp-overview" aria-label="Die wichtigsten Unterschiede">' +
      '<h3>Auf einen Blick</h3>' +
      rows.map(([label, key]) => '<div class="vmrasse__cmp-overview-row"><span class="vmrasse__cmp-overview-label">' + escapeHtml(label) + '</span><div class="vmrasse__cmp-overview-values" style="grid-template-columns:repeat(' + items.length + ',minmax(0,1fr))">' +
        items.map(it => '<span class="vmrasse__cmp-overview-value">' + escapeHtml(labels[it[key]] || it[key] || 'Keine Angabe') + '</span>').join('') +
        '</div></div>').join('') +
      '</section>';
  }

  function openCompareModal() {
    if (STATE.compare.size < 1) return;
    const modal = root.querySelector('[data-vmrasse-modal]');
    const body = root.querySelector('[data-vmrasse-compare-body]');
    if (!modal || !body) return;
    const items = Array.from(STATE.compare).map(id => DATA.find(x => x.profile_id === id)).filter(Boolean);
    if (items.length < 1) return;

    // Mobile-Tab-Bar (nur sichtbar < 768px via CSS)
    const tabsHtml = items.length > 1
      ? '<div class="vmrasse__cmp-tabs" role="tablist">' +
        items.map((it, idx) => {
          const canonical = (it.title || '').split(':')[0].trim();
          return '<button type="button" class="vmrasse__cmp-tab' + (idx === 0 ? ' vmrasse__cmp-tab--active' : '') + '" data-cmp-tab="' + idx + '" role="tab" aria-selected="' + (idx === 0 ? 'true' : 'false') + '">' + escapeHtml(canonical) + '</button>';
        }).join('') +
        '</div>'
      : '';

    // Profil-Header-Reihe
    const headersHtml = '<div class="vmrasse__cmp-heads" data-cmp-cols="' + items.length + '">' +
      items.map(renderProfileHeader).join('') +
      '</div>';

    // Sektionen
    const sectionA = renderSection('A', 'Charakter & Arbeit', items, [
      ['Kooperation',         'score_kooperation',        'Wie kooperativ-menschorientiert die Rasse arbeitet. Hoch = team-/menschenfokussiert.'],
      ['Autonomie',           'score_autonomie',          'Selbstständigkeit in Entscheidungen. Hoch = trifft eigene Entscheidungen unabhängig vom Halter.'],
      ['Wach/Schutz',         'score_wach_schutz',        'Wach- und Verteidigungsverhalten gegenüber Fremden / Territorium.'],
      ['Reizempfindlichkeit', 'score_reizempfindlichkeit','Wie schnell die Rasse auf Außenreize reagiert. Hoch = reizoffen.'],
      ['Frust/Erregung',      'score_frust_erregung',     'Erregbarkeit und Frustrationsrisiko. Hoch = braucht aktives Management.'],
    ]);

    const sectionB = renderSection('B', 'Konflikt & Aggression', items, [
      ['Menschen-Risiko Familie', 'score_menschenrisiko_familie', 'Aggressions-Risiko gegenüber engen Bezugspersonen / Kindern.'],
      ['Menschen-Risiko Fremd',   'score_menschenrisiko_fremd',   'Aggressions-Risiko gegenüber Fremden, Besuch, Passanten.'],
      ['Artgenossen-Risiko',      'score_artgenossenrisiko',      'Konflikt-Risiko gegenüber anderen Hunden.'],
      ['Gefahrenpotenzial',      'score_gefahrenpotenzial',     'Zusammenfassender Profilwert; ersetzt keine Einschätzung des einzelnen Hundes.'],
    ], {
      quotesField: 'aggressionsprofil_kurz',
      methodikHinweis: 'Aggressions-Sub-Scores beschreiben Risiken direkter Aggression gegenüber typischen Interaktions-Partnern. Spezifische Trieb- und Schadens-Risiken siehe Sektion D.',
    });

    const sectionC = renderSection('C', 'Trieb & Jagd', items, [
      ['Jagdtrieb', 'score_jagd', 'Stärke des Jagdverhaltens entlang der Beute-Kette: Sehen → Verfolgen → Packen.'],
    ], {
      erklaerungsBox: 'Jagdtrieb umfasst die Beute-Kette: Sehen → Verfolgen → Packen. Bei Hütehunden in modifizierter Form (ohne Tötung).',
    });

    const sectionD = renderSection('D', 'Trieb & Schadens-Risiko', items, [
      ['Beutefang',      'score_beutefang',      'Trieb-Subkomponente Beutefang (Schnappen, Packen) — bei Hütehunden in modifizierter Form.'],
      ['Nutztier-Risiko','score_nutztierrisiko', 'Schadens-Risiko gegenüber Schafen, Rindern, Hühnern. Wichtig für Halter mit Nutztier-Kontext.'],
    ], {
      erklaerungsBox: 'Diese Scores beschreiben Trieb-Phänomene und reale Schadens-Risiken — nicht Aggression. Gerade Hütehunde und jagdlich geführte Rassen zeigen hier hohe Werte ohne aggressive Motivation.',
    });

    // Aktionen
    const actionsHtml = '<div class="vmrasse__cmp-actions">' +
      '<button type="button" class="vmrasse__cmp-action vmrasse__cmp-action--secondary" data-vmrasse-compare-clear>Auswahl zurücksetzen</button>' +
      '<button type="button" class="vmrasse__cmp-action" data-vmrasse-modal-close>Schließen</button>' +
      '</div>';

    body.innerHTML = tabsHtml + headersHtml + renderOverview(items) + sectionA + sectionB + sectionC + sectionD + actionsHtml;

    // Mobile-Tabs verdrahten
    body.querySelectorAll('[data-cmp-tab]').forEach(btn => {
      btn.addEventListener('click', () => {
        const idx = parseInt(btn.getAttribute('data-cmp-tab'), 10);
        body.querySelectorAll('.vmrasse__cmp-tab').forEach((b, i) => {
          b.classList.toggle('vmrasse__cmp-tab--active', i === idx);
          b.setAttribute('aria-selected', i === idx ? 'true' : 'false');
        });
        body.setAttribute('data-active-tab', String(idx));
      });
    });
    body.setAttribute('data-active-tab', '0');

    // URL-Hash setzen für Sharing
    const handles = items.map(it => {
      try { return new URL(it.url, location.origin).pathname.split('/').pop(); }
      catch (e) { return it.profile_id; }
    });
    location.hash = 'compare=' + handles.join(',');

    modal.setAttribute('data-visible', 'true');
    document.body.style.overflow = 'hidden';
    // Phase 3.5.E: Focus-Trap aktivieren + initial Focus auf Close-Button
    const closeBtn = modal.querySelector('[data-vmrasse-modal-close]');
    if (closeBtn) closeBtn.focus();
    if (!modal.__vmrTrap) {
      modal.__vmrTrap = (e) => trapFocus(modal, e);
      modal.addEventListener('keydown', modal.__vmrTrap);
    }
  }

  function closeCompareModal() {
    const modal = root.querySelector('[data-vmrasse-modal]');
    if (modal) {
      modal.setAttribute('data-visible', 'false');
      // Phase 3.5.E: Focus-Trap deaktivieren
      if (modal.__vmrTrap) {
        modal.removeEventListener('keydown', modal.__vmrTrap);
        modal.__vmrTrap = null;
      }
    }
    document.body.style.overflow = '';
    if (location.hash.startsWith('#compare=')) {
      history.replaceState(null, '', location.pathname + location.search);
    }
  }

  // URL-Hash beim Init lesen
  function restoreCompareFromHash() {
    if (!location.hash.startsWith('#compare=')) return;
    const handles = decodeURIComponent(location.hash.slice('#compare='.length)).split(',').filter(Boolean);
    if (!handles.length) return;
    STATE.compare.clear();
    for (const h of handles) {
      const p = DATA.find(d => {
        try { return new URL(d.url, location.origin).pathname.split('/').pop() === h; }
        catch (e) { return d.profile_id === h; }
      });
      if (p && STATE.compare.size < 3) STATE.compare.add(p.profile_id);
    }
    if (STATE.compare.size > 0) {
      renderCompareBar();
      // Auto-open Modal nach kurzer Wartezeit für Layout-Stabilität
      setTimeout(openCompareModal, 200);
    }
  }

  // ---------------------------------------------------- Phase 3.5: Focus-Trap (Modal A11y)
  function trapFocus(container, evt) {
    if (evt.key !== 'Tab') return;
    const focusables = container.querySelectorAll('button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (evt.shiftKey && document.activeElement === first) {
      evt.preventDefault(); last.focus();
    } else if (!evt.shiftKey && document.activeElement === last) {
      evt.preventDefault(); first.focus();
    }
  }

  // Phase 3.5: Apply-Button im Drawer (Mobile/Tablet) — schließt Drawer + scrollt zu Cards
  function wireSidebarApply() {
    const applyBtn = root.querySelector('[data-vmrasse-sidebar-apply]');
    if (!applyBtn) return;
    applyBtn.addEventListener('click', () => {
      const sidebar = root.querySelector('[data-vmrasse-sidebar]');
      const backdrop = root.querySelector('[data-vmrasse-sidebar-backdrop]');
      if (sidebar) sidebar.setAttribute('data-open', 'false');
      if (backdrop) backdrop.setAttribute('data-visible', 'false');
      document.body.style.overflow = '';
      const grid = root.querySelector('[data-vmrasse-grid]');
      if (grid) grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  // ---------------------------------------------------- Phase 3.1: Sidebar-Drawer + Sortier-UI
  function wireSidebarDrawer() {
    const sidebar  = root.querySelector('[data-vmrasse-sidebar]');
    const backdrop = root.querySelector('[data-vmrasse-sidebar-backdrop]');
    const openBtns = root.querySelectorAll('[data-vmrasse-sidebar-open]');
    const closeBtn = root.querySelector('[data-vmrasse-sidebar-close]');
    if (!sidebar) return;

    function open() {
      sidebar.setAttribute('data-open', 'true');
      if (backdrop) {
        backdrop.removeAttribute('hidden');
        backdrop.setAttribute('data-visible', 'true');
      }
      openBtns.forEach(b => b.setAttribute('aria-expanded', 'true'));
      document.body.style.overflow = 'hidden';
    }
    function close() {
      sidebar.setAttribute('data-open', 'false');
      if (backdrop) {
        backdrop.setAttribute('data-visible', 'false');
        // Hide nach Transition
        setTimeout(() => backdrop.setAttribute('hidden', ''), 220);
      }
      openBtns.forEach(b => b.setAttribute('aria-expanded', 'false'));
      document.body.style.overflow = '';
    }
    openBtns.forEach(b => b.addEventListener('click', open));
    if (closeBtn) closeBtn.addEventListener('click', close);
    if (backdrop) backdrop.addEventListener('click', close);
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && sidebar.getAttribute('data-open') === 'true') close();
    });
  }

  function wireSortDropdown() {
    const sortSelect = root.querySelector('[data-vmrasse-sort]');
    const sortMobileBtn = root.querySelector('[data-vmrasse-sort-open]');
    if (!sortSelect) return;
    // Phase 3.3: Sort-Wert von URL initialisieren
    const sp = new URLSearchParams(location.search);
    const initialSort = sp.get('sort');
    if (initialSort && ['alpha','qualzucht','aktivitaet','groesse'].includes(initialSort)) {
      STATE.sort = initialSort;
      sortSelect.value = initialSort;
    }
    sortSelect.addEventListener('change', () => {
      const v = sortSelect.value;
      STATE.sort = v;
      // URL-State
      const sp = new URLSearchParams(location.search);
      if (v && v !== 'alpha') sp.set('sort', v); else sp.delete('sort');
      history.replaceState({}, '', location.pathname + (sp.toString() ? '?' + sp : '') + location.hash);
      applyFilters();
    });
    if (sortMobileBtn) {
      sortMobileBtn.addEventListener('click', () => {
        sortSelect.scrollIntoView({ behavior: 'smooth', block: 'center' });
        sortSelect.focus();
      });
    }
  }

  // ---------------------------------------------------- Phase 3.3 v2: Smart-Filter-Tiles
  // Atomare Filter-Sets pro Tile. Jede Smart-Aktion ersetzt aktuelle Filter (clear → set).
  // Bedingungen, die die Engine noch nicht direkt kann (z.B. != / ≤), werden als Set-Membership
  // mit erlaubten Werten kodiert. Komplexere Logik (z.B. score_menschenrisiko_familie ≤ 2)
  // wird über STATE.smartActive zusätzlich berücksichtigt.
  const SMART_PRESETS = {
    familienhund: {
      label: 'Familienhund',
      filters: { anfaenger: ['ja','bedingt'], kinder: ['ja'], qualzucht: ['grün','gelb'] },
      extraTest: p => (parseFloat(p.score_menschenrisiko_familie || 0) <= 2),
    },
    'stadt-anfaenger': {
      label: 'Stadt-Anfänger',
      filters: { anfaenger: ['ja','bedingt'], groesse: ['sehr_klein','klein','klein_mittel','mittel'] },
      extraTest: p => p.eignung_stadt === 'ja' && (p.qualzucht_ampel === 'grün' || p.qualzucht_ampel === 'gelb'),
    },
    'sport-partner': {
      label: 'Sport-Partner',
      filters: { aktivitaet: ['hoch','sehr_hoch'] },
      extraTest: p => (p.eignung_freilauf === 'ja' || p.eignung_freilauf === 'bedingt'),
    },
    listenhund: {
      label: 'Schutz & Terrier',
      filters: { vit: ['VIT-02','VIT-03','VIT-05'] },
      extraTest: null,
    },
    tierschutz: {
      label: 'Tierschutz im Blick',
      filters: { qualzucht: ['orange','rot'] },
      extraTest: null,
    },
    nutztiere: {
      label: 'Mit Nutztieren',
      filters: { nutztier: ['1','2'] },
      extraTest: p => (parseFloat(p.score_jagd || 5) <= 3),
    },
  };

  let activeSmart = null;  // current Smart-Preset (oder null)

  // Erweiterte profileMatches die activeSmart berücksichtigt (extraTest)
  const _baseMatches = profileMatches;
  profileMatches = function(p) {
    if (!_baseMatches(p)) return false;
    if (activeSmart && SMART_PRESETS[activeSmart]) {
      const preset = SMART_PRESETS[activeSmart];
      if (preset.extraTest) {
        if (!preset.extraTest(p)) return false;
      }
    }
    return true;
  };

  function applySmartPreset(presetKey) {
    const preset = SMART_PRESETS[presetKey];
    if (!preset) return;
    // Reset alle Filter
    Object.keys(STATE.filters).forEach(k => STATE.filters[k].clear());
    STATE.search = '';
    const searchInput = root.querySelector('[data-vmrasse-search-input]');
    if (searchInput) searchInput.value = '';
    // Set Filter-Werte
    for (const [k, vals] of Object.entries(preset.filters)) {
      if (STATE.filters[k]) vals.forEach(v => STATE.filters[k].add(v));
    }
    activeSmart = presetKey;
    // URL
    const sp = new URLSearchParams(location.search);
    sp.set('preset', presetKey);
    history.replaceState({}, '', location.pathname + (sp.toString() ? '?' + sp : '') + location.hash);
    writeUrl();
    applyFilters();
    syncPillsFromState();
    syncSmartTilesFromState();
    // Scroll zu Cards
    const grid = root.querySelector('[data-vmrasse-grid]');
    if (grid) grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function syncSmartTilesFromState() {
    root.querySelectorAll('[data-vmrasse-smart]').forEach(t => {
      const k = t.getAttribute('data-vmrasse-smart');
      t.setAttribute('aria-pressed', activeSmart === k ? 'true' : 'false');
    });
  }

  function syncPillsFromState() {
    root.querySelectorAll('[data-vmrasse-filter]').forEach(p => {
      const k = p.getAttribute('data-vmrasse-filter');
      const v = p.getAttribute('data-vmrasse-value');
      if (STATE.filters[k] && STATE.filters[k].has(v)) {
        p.setAttribute('aria-pressed', 'true');
      } else {
        p.setAttribute('aria-pressed', 'false');
      }
    });
    updateGroupBadges();
  }

  function updateGroupBadges() {
    root.querySelectorAll('[data-vmrasse-group-count]').forEach(b => {
      const k = b.getAttribute('data-vmrasse-group-count');
      const n = STATE.filters[k] ? STATE.filters[k].size : 0;
      b.textContent = n;
      if (n > 0) { b.removeAttribute('hidden'); }
      else { b.setAttribute('hidden', ''); }
    });
  }

  function wireSmartTiles() {
    root.querySelectorAll('[data-vmrasse-smart]').forEach(tile => {
      tile.addEventListener('click', () => {
        const k = tile.getAttribute('data-vmrasse-smart');
        // Toggle: wenn schon aktiv, deaktivieren (Filter bleiben aber)
        if (activeSmart === k) {
          resetAll();
          syncSmartTilesFromState();
        } else {
          applySmartPreset(k);
        }
      });
    });
    // From URL
    const sp = new URLSearchParams(location.search);
    const preset = sp.get('preset');
    if (preset && SMART_PRESETS[preset]) {
      applySmartPreset(preset);
    }
  }

  function wireSidebarSearchClear() {
    const input = root.querySelector('[data-vmrasse-search-input]');
    const clearBtn = root.querySelector('[data-vmrasse-search-clear]');
    if (!input) return;
    function refreshClear() {
      if (clearBtn) {
        if (input.value) clearBtn.removeAttribute('hidden');
        else clearBtn.setAttribute('hidden', '');
      }
    }
    input.addEventListener('input', refreshClear);
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        input.value = '';
        STATE.search = '';
        const sp = new URLSearchParams(location.search);
        sp.delete('q');
        history.replaceState({}, '', location.pathname + (sp.toString() ? '?' + sp : '') + location.hash);
        refreshClear();
        applyFilters();
        input.focus();
      });
    }
    refreshClear();
  }

  // Wrap applyFilters um updateGroupBadges am Ende
  const _baseApplyFilters = applyFilters;
  applyFilters = function() {
    _baseApplyFilters();
    updateGroupBadges();
  };

  // ---------------------------------------------------- Init
  // Phase 3.5 init — wireSidebarApply für Mobile-Drawer-Apply-Button, plus alle vorigen Wires
  readUrl();
  wire();
  wireSidebarDrawer();
  wireSidebarApply();
  wireSortDropdown();
  wireSmartTiles();
  wireSidebarSearchClear();
  applyFilters();
  renderCompareBar();
  restoreCompareFromHash();
})();
// vmrasse.js Phase 3.6 build 2026-05-04T-final-qa
// vmrasse.js Phase 3.3 v2 build 2026-05-04T-final
